import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { appConfig } from 'src/config/appConfig';
import type { Raffle, RaffleFavorite, UserStats } from 'src/types/raffle';

export function useUserRaffles(userId: string | null) {
  const [createdRaffles, setCreatedRaffles] = useState<Raffle[]>([]);
  const [joinedRaffles, setJoinedRaffles] = useState<Raffle[]>([]);
  const [favoriteRaffles, setFavoriteRaffles] = useState<RaffleFavorite[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);

  const [loadingCreated, setLoadingCreated] = useState(false);
  const [loadingJoined, setLoadingJoined] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchCreated = useCallback(async () => {
    if (!userId) return;
    setLoadingCreated(true);
    try {
      const res = await axios.get(`${appConfig.serviceUrl}/users/${userId}/raffles/created`);
      setCreatedRaffles(res.data.data ?? []);
    } catch {
      setCreatedRaffles([]);
    } finally {
      setLoadingCreated(false);
    }
  }, [userId]);

  const fetchJoined = useCallback(async () => {
    if (!userId) return;
    setLoadingJoined(true);
    try {
      const res = await axios.get(`${appConfig.serviceUrl}/users/${userId}/raffles/joined`);
      setJoinedRaffles(res.data.data ?? []);
    } catch {
      setJoinedRaffles([]);
    } finally {
      setLoadingJoined(false);
    }
  }, [userId]);

  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    setLoadingFavorites(true);
    try {
      const res = await axios.get(`${appConfig.serviceUrl}/users/${userId}/raffles/favorites`);
      setFavoriteRaffles(res.data.data ?? []);
    } catch {
      setFavoriteRaffles([]);
    } finally {
      setLoadingFavorites(false);
    }
  }, [userId]);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    setLoadingStats(true);
    try {
      const res = await axios.get(`${appConfig.serviceUrl}/users/${userId}/stats`);
      setStats(res.data);
    } catch {
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchCreated();
      fetchJoined();
      fetchFavorites();
      fetchStats();
    }
  }, [userId, fetchCreated, fetchJoined, fetchFavorites, fetchStats]);

  return {
    createdRaffles,
    joinedRaffles,
    favoriteRaffles,
    stats,
    loadingCreated,
    loadingJoined,
    loadingFavorites,
    loadingStats,
    fetchCreated,
    fetchJoined,
    fetchFavorites,
    fetchStats,
  };
}
